import { Fragment, ReactNode } from 'react';

/**
 * Shows the `backticked` parts of a sentence as code, the one bit of markup a suite author can use.
 *
 * An odd number of backticks leaves the last one as it is rather than swallowing the rest of the sentence.
 */
export const withInlineCode = (text: string): ReactNode => {
    const parts = text.split('`');

    if (parts.length < 3) {
        return text;
    }

    const closed = parts.length % 2 === 1 ? parts : parts.slice(0, -1);
    const trailing = closed === parts ? '' : '`' + parts[parts.length - 1];

    return (
        <>
            {closed.map((part, index) =>
                index % 2 === 1 ? (
                    <code key={index}>{part}</code>
                ) : (
                    <Fragment key={index}>{part}</Fragment>
                )
            )}
            {trailing}
        </>
    );
};

export interface IStepGroup {
    system: string;
    steps: string[];
}

/**
 * Groups steps written as "System: what to do" by that system, systems in alphabetical order.
 *
 * A step without such a prefix goes under "Other", last, instead of being dropped or guessed at.
 */
export const groupBySystem = (steps: string[]): IStepGroup[] => {
    const groups: IStepGroup[] = [];

    steps.forEach((step) => {
        const match = /^([A-Za-z][\w.-]{0,30}):\s+(.+)$/.exec(step);
        const system = match ? match[1] : 'Other';
        const text = match ? match[2] : step;
        const group = groups.find((candidate) => candidate.system === system);

        if (group) {
            group.steps.push(text);
        } else {
            groups.push({ system, steps: [text] });
        }
    });

    return groups.sort((a, b) =>
        a.system === 'Other'
            ? 1
            : b.system === 'Other'
            ? -1
            : a.system.localeCompare(b.system)
    );
};
